// import * as dat from 'dat.gui'; // disabled for prod, restore for Three.js dev
import GSAP from 'gsap';

// One-shot intro for the home gallery. The wall AUTO-SCROLLS the whole time (the
// gallery's normal autoplay runs from frame 1), the camera flies from a tilted /
// zoomed-out / lowered pose to identity, and the images fade + scale + rotate into
// place from the center outward. So there are three motions at once: camera,
// self-scrolling wall, and the per-image reveal.
//
// While the intro plays the gallery is `introActive`: user input (wheel/touch/
// keys) is locked and analytics are paused, but autoplay is NOT frozen. finish()
// clears introActive → input + analytics resume; the camera/tf are at identity so
// steady-state is unchanged.
//
// No vertical (y) tween on the images — that would fight the auto-scroll. Only
// fade / scale / rotate / a little x. All tunables live in this.params (dat.GUI;
// Replay rebuilds the timeline). gallery.tf[] is uploaded by Experience via
// gallery.syncTf() each frame while this.timeline.isActive().
export default class IntroTimeline {
	constructor({ gallery, camera, sizes, gui = true }) {
		this.gallery = gallery;
		this.camera = camera; // THREE.OrthographicCamera
		this.sizes = sizes;
		this.tf = gallery.tf || [];

		this.params = {
			// timing
			DUR: 3, // total intro (s)
			itemDur: 0.6, // per-image settle (s)
			// camera start pose (ends at identity)
			tilt: 0.2, // rad
			roll: 0.03, // rad
			zoom: 0.1, // <1 = out
			posY: -10080, // start height (px); -down/+up. ends at 0
			camEase: 'power2.inOut',
			// per-image reveal (settle to identity) — NO y, autoplay owns vertical
			itemXRand: 30, // px random
			itemRotRand: 0.08, // rad random
			itemScale: 0.9, // start scale
			itemEase: 'power3.out',
		};

		this.buildTimeline();
		// if (gui) this.buildGui(); // dat.gui disabled for prod, restore for Three.js dev
	}

	vh() {
		return (this.sizes ? this.sizes.height : window.innerHeight) || 1;
	}

	buildTimeline() {
		const cam = this.camera;
		const tf = this.tf;
		const p = this.params;
		const vh = this.vh();

		const SPREAD = Math.max(0, p.DUR - p.itemDur); // window staggers start within

		this.gallery.introActive = true; // lock input + pause analytics for the intro
		const tl = GSAP.timeline({ paused: true });

		// reveal order: nearest the screen-center first, fanning outward
		const order = this.gallery.imageBounds
			.map((o, i) => {
				const cy = (o.bounds.top + o.bounds.bottom) / 2;
				return { i, d: Math.abs(cy - vh / 2) };
			})
			.sort((a, b) => a.d - b.d);
		const rank = [];
		order.forEach((o, r) => (rank[o.i] = r));
		const maxRank = order.length - 1 || 1;

		// camera: tilt + roll + lower + zoom-out -> identity
		if (cam) {
			this.applyStartPose();
			tl.fromTo(
				cam.rotation,
				{ x: p.tilt, z: p.roll },
				{ x: 0, z: 0, duration: p.DUR, ease: p.camEase, immediateRender: true },
				0
			);
			tl.fromTo(
				cam.position,
				{ y: p.posY },
				{ y: 0, duration: p.DUR, ease: p.camEase, immediateRender: true },
				0
			);
			tl.fromTo(
				cam,
				{ zoom: p.zoom },
				{
					zoom: 1,
					duration: p.DUR,
					ease: p.camEase,
					immediateRender: true,
					onUpdate: () => cam.updateProjectionMatrix(),
				},
				0
			);
		}

		// images: faded + scaled-down + slight x/rot -> settle to rest, center-out.
		// No y: the wall is already auto-scrolling vertically underneath.
		const rand = (a) => (Math.random() * 2 - 1) * a;
		tf.forEach((t, i) => {
			Object.assign(t, {
				x: rand(p.itemXRand),
				y: 0,
				rot: rand(p.itemRotRand),
				scale: p.itemScale,
				alpha: 0,
			});
			tl.to(
				t,
				{
					x: 0,
					y: 0,
					rot: 0,
					scale: 1,
					alpha: 1,
					duration: p.itemDur,
					ease: p.itemEase,
				},
				(rank[i] / maxRank) * SPREAD
			);
		});

		// push the start state (alpha 0) now so frame 1 is already faded out
		this.gallery.syncTf();

		tl.eventCallback('onComplete', () => this.finish());

		this.timeline = tl;
	}

	// Pose the camera at the intro start (used at build + live GUI preview).
	applyStartPose() {
		const cam = this.camera;
		const p = this.params;
		if (!cam) return;
		cam.rotation.x = p.tilt;
		cam.rotation.z = p.roll;
		cam.position.y = p.posY;
		cam.zoom = p.zoom;
		cam.updateProjectionMatrix();
	}

	// Land cleanly: identity camera, identity tf (one last upload), unlock input.
	finish() {
		const cam = this.camera;
		if (cam) {
			cam.rotation.x = 0;
			cam.rotation.z = 0;
			cam.position.y = 0;
			cam.zoom = 1;
			cam.updateProjectionMatrix();
		}
		this.tf.forEach((t) =>
			Object.assign(t, { x: 0, y: 0, rot: 0, scale: 1, alpha: 1 })
		);
		this.gallery.syncTf();
		this.gallery.introActive = false;
		this.gallery.isPlayed = true;
	}

	play() {
		if (this.timeline) this.timeline.play();
	}

	// Rebuild the timeline with the current params and play from the start.
	replay() {
		if (this.timeline) this.timeline.kill();
		this.buildTimeline();
		this.timeline.play();
	}

	buildGui() {
		// dat.gui disabled for prod — body commented out, restore for Three.js dev
		/*
		this.gui = new dat.GUI({ width: 300 });
		this.gui.domElement.style.zIndex = 999999;
		const p = this.params;

		const preview = () => this.applyStartPose(); // live-preview the start pose

		const fCam = this.gui.addFolder('Camera (start → identity)');
		fCam.add(p, 'tilt', -1.2, 1.2, 0.01).name('tilt (rad)').onChange(preview);
		fCam.add(p, 'roll', -0.5, 0.5, 0.01).name('roll (rad)').onChange(preview);
		fCam.add(p, 'zoom', 0.05, 1.5, 0.01).name('zoom').onChange(preview);
		fCam
			.add(p, 'posY', -15000, 15000, 100)
			.name('pos Y (down/up)')
			.onChange(preview);
		fCam
			.add(p, 'camEase', [
				'power1.inOut',
				'power2.inOut',
				'power3.inOut',
				'power4.inOut',
				'expo.inOut',
				'power2.out',
				'none',
			])
			.name('cam ease');
		fCam.open();

		const fMot = this.gui.addFolder('Motion / timing');
		fMot.add(p, 'DUR', 0.3, 5, 0.05).name('duration (s)');
		fMot.add(p, 'itemDur', 0.1, 3, 0.05).name('item dur (s)');
		fMot.open();

		const fItem = this.gui.addFolder('Items (reveal)');
		fItem.add(p, 'itemXRand', 0, 300, 5).name('x random (px)');
		fItem.add(p, 'itemRotRand', 0, 0.6, 0.01).name('rot random (rad)');
		fItem.add(p, 'itemScale', 0.1, 2, 0.01).name('start scale');
		fItem
			.add(p, 'itemEase', [
				'power1.out',
				'power2.out',
				'power3.out',
				'power4.out',
				'expo.out',
				'back.out',
			])
			.name('item ease');
		fItem.open();

		const acts = {
			Replay: () => this.replay(),
			Pause: () => this.timeline && this.timeline.pause(),
			Resume: () => this.timeline && this.timeline.resume(),
			'Log params': () => console.log(JSON.parse(JSON.stringify(p))),
		};
		const fAct = this.gui.addFolder('Actions');
		Object.keys(acts).forEach((k) => fAct.add(acts, k));
		fAct.open();
		*/
	}

	destroy() {
		if (this.timeline) this.timeline.kill();
		this.timeline = null;
		if (this.gui) {
			this.gui.destroy();
			this.gui = null;
		}
	}
}
